import React, { useState, useEffect } from 'react';
import { 
  Users, Package, UserPlus, FileText, Webhook, HardDrive,
  TrendingUp, AlertTriangle, CheckCircle, ArrowUpRight,
  RefreshCw, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API_URL = '';

const RESOURCE_ICONS = {
  clients: Users,
  services: Package,
  team_members: UserPlus,
  kb_articles: FileText,
  webhooks: Webhook,
  storage: HardDrive
};

const RESOURCE_LABELS = {
  clients: 'Clients',
  services: 'Services',
  team_members: 'Team Members',
  kb_articles: 'KB Articles',
  webhooks: 'Webhooks',
  storage: 'Storage'
};

const RESOURCE_DESCRIPTIONS = {
  clients: 'Number of client accounts in your portal',
  services: 'Products/services you can offer',
  team_members: 'Admin and support team members',
  kb_articles: 'Knowledge base articles for self-service',
  webhooks: 'Outgoing webhook integrations',
  storage: 'Database storage used by your portal'
};

function UsageCard({ resourceKey, data }) {
  const Icon = RESOURCE_ICONS[resourceKey] || Package;
  const label = RESOURCE_LABELS[resourceKey] || resourceKey;
  const description = RESOURCE_DESCRIPTIONS[resourceKey] || '';
  
  const isUnlimited = data.limit === '∞' || data.limit >= 999999;
  const percentage = isUnlimited ? 0 : Math.min(data.percentage, 100);
  const isWarning = percentage >= 80 && percentage < 100;
  const isAtLimit = data.at_limit;
  
  const getStatusColor = () => {
    if (isAtLimit) return 'text-red-500';
    if (isWarning) return 'text-amber-500';
    return 'text-green-500';
  };
  
  const getProgressColor = () => {
    if (isAtLimit) return 'bg-red-500';
    if (isWarning) return 'bg-amber-500';
    return 'bg-indigo-500';
  };
  
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isAtLimit ? 'bg-red-100' : isWarning ? 'bg-amber-100' : 'bg-indigo-100'}`}>
              <Icon className={`w-5 h-5 ${isAtLimit ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-indigo-600'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">{label}</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-3 h-3 text-slate-400 ml-1" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">{description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          {isAtLimit ? (
            <Badge variant="destructive" className="text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              At Limit
            </Badge>
          ) : isWarning ? (
            <Badge className="bg-amber-100 text-amber-700 text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Near Limit
            </Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700 text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              OK
            </Badge>
          )}
        </div>
        
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-slate-900">
              {data.current}
              {resourceKey === 'storage' && <span className="text-sm font-normal text-slate-500 ml-1">MB</span>}
            </span>
            <span className="text-sm text-slate-500">
              of {isUnlimited ? '∞' : data.limit}{resourceKey === 'storage' ? ' MB' : ''}
            </span>
          </div>
          
          {!isUnlimited && (
            <div className="relative">
              <Progress 
                value={percentage} 
                className="h-2 bg-slate-100"
              />
              <div 
                className={`absolute top-0 left-0 h-2 rounded-full transition-all ${getProgressColor()}`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          )}
          
          {!isUnlimited && (
            <p className="text-xs text-slate-500">
              {percentage.toFixed(1)}% used
              {!isAtLimit && ` • ${data.limit - data.current} remaining`}
            </p>
          )}
          
          {isUnlimited && (
            <p className="text-xs text-slate-500">Unlimited on your current plan</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function UpgradeBenefitsCard({ benefits, onUpgrade }) {
  if (!benefits || benefits.message) {
    return null;
  }
  
  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-indigo-900">
          <TrendingUp className="w-5 h-5" />
          Upgrade to {benefits.next_tier?.replace('tier', 'Tier ')}
        </CardTitle>
        <CardDescription className="text-indigo-700">
          Reduce your platform fee from {benefits.fee_reduction}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {benefits.improvements?.map((improvement, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <span className="text-slate-700">{improvement.feature}</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">{improvement.current}</span>
                <ArrowUpRight className="w-4 h-4 text-indigo-500" />
                <span className="font-medium text-indigo-700">{improvement.upgraded}</span>
              </div>
            </div>
          ))}
        </div>
        
        {onUpgrade && (
          <Button 
            className="w-full mt-6 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600"
            onClick={onUpgrade}
          >
            Upgrade Now
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function UsageDashboard({ tenantId, token }) {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [benefits, setBenefits] = useState(null);
  const [error, setError] = useState(null);
  
  const fetchUsage = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/usage/${tenantId}/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
        
        // Fetch upgrade benefits if near limits
        if (data.needs_upgrade) {
          const benefitsResponse = await fetch(`${API_URL}/api/usage/${tenantId}/upgrade-benefits`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (benefitsResponse.ok) {
            setBenefits(await benefitsResponse.json());
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to load usage data');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Failed to Load Usage Data</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Button variant="outline" onClick={fetchUsage}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  if (!usage) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Info className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700">No Usage Data Available</h3>
          <p className="text-slate-500">Usage tracking data is not available for your account.</p>
        </CardContent>
      </Card>
    );
  }
  
  // Order resources for display
  const resourceOrder = ['clients', 'services', 'team_members', 'kb_articles', 'webhooks', 'storage'];
  
  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Resource Usage</h3>
          <p className="text-sm text-slate-500">
            Current plan: <span className="font-medium">{usage.tier?.replace('tier', 'Tier ')}</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchUsage}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Alert if near or at limits */}
      {usage.needs_upgrade && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-900">
                  You're approaching your plan limits
                </p>
                <p className="text-sm text-amber-700">
                  Consider upgrading to {usage.upgrade_to?.replace('tier', 'Tier ')} for higher limits and lower platform fees.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Usage Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resourceOrder.map(key => {
          const data = usage.resources?.[key];
          if (!data) return null;
          return <UsageCard key={key} resourceKey={key} data={data} />;
        })}
      </div>
      
      {/* Upgrade Benefits */}
      {benefits && (
        <UpgradeBenefitsCard 
          benefits={benefits} 
          onUpgrade={() => {
            // TODO: Implement upgrade flow
            alert('Contact support to upgrade your plan.');
          }}
        />
      )}
    </div>
  );
}
